import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { emptyNotificationPreferences, NotificationPreferences } from '../../models/notification-preferences.model';
import { ContextService } from '../../services/context.service';
import { NotificationPreferencesService } from '../../services/notification-preferences.service';
import { NotificationPreferencesFormComponent } from '../notification-preferences-form/notification-preferences-form.component';
import { AUTH_CONFIG } from '../../auth.config';
import {
  applyFirstLoginNotificationDefaults,
  buildSaveNotificationPreferencesRequest,
  createNotificationPreferencesForm,
  syncNotificationPreferencesForm
} from '../../utils/notification-preferences-form.utils';
import { notificationPreferencesValidationError, notificationPreferencesCompanyName } from '../../utils/notification-preferences.helpers';
import { firstValueFrom } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { needsClientPortalSelection } from '../../utils/portal-login-scope';

@Component({
  selector: 'lr-notification-setup',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NotificationPreferencesFormComponent],
  templateUrl: './notification-setup.component.html',
  styleUrls: ['./notification-setup.component.scss']
})
export class NotificationSetupComponent implements OnInit {
  contextService = inject(ContextService);
  private notificationPreferencesService = inject(NotificationPreferencesService);
  private router = inject(Router);
  private config = inject(AUTH_CONFIG);

  readonly isLoading = signal(true);
  readonly isSaving = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly notificationPreferences = signal<NotificationPreferences>(emptyNotificationPreferences());

  readonly notificationForm = createNotificationPreferencesForm();

  async ngOnInit(): Promise<void> {
    if (this.config.portalKey.toLowerCase() !== 'client') {
      this.contextService.clearPendingNotificationSetup();
      await this.router.navigate(['/home'], { replaceUrl: true });
      return;
    }

    if (!this.contextService.isPendingNotificationSetup()) {
      await this.router.navigate(['/dashboard'], { replaceUrl: true });
      return;
    }

    if (!this.ensureClientSelected()) {
      await this.router.navigate(['/client-selection'], { replaceUrl: true });
      return;
    }

    await this.loadForSelectedClient();
  }

  private scopeMCode(): string | null {
    return this.contextService.getSelectedClient()?.mCode ?? null;
  }

  companyDisplayName(): string | null {
    return notificationPreferencesCompanyName(
      this.notificationPreferences(),
      this.contextService.getSelectedClient()?.name
    );
  }

  hasMultipleCompanies(): boolean {
    return this.contextService.getClients().length > 1;
  }

  private ensureClientSelected(): boolean {
    if (this.contextService.getSelectedClient()) {
      return true;
    }

    const clients = this.contextService.getClients();
    if (clients.length === 0) {
      return false;
    }

    const roles = this.contextService.getRoles();
    if (needsClientPortalSelection(roles, clients.length)) {
      return false;
    }

    this.contextService.setSelectedClient(clients[0].mCode);
    return true;
  }

  private async loadForSelectedClient(): Promise<void> {
    const mCode = this.scopeMCode();
    if (!mCode) {
      this.isLoading.set(false);
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set(null);
    try {
      const preferences = await firstValueFrom(
        this.notificationPreferencesService.getByMCode(mCode).pipe(timeout(20000))
      );
      this.notificationPreferences.set(preferences);
      syncNotificationPreferencesForm(this.notificationForm, preferences);
      applyFirstLoginNotificationDefaults(this.notificationForm);
    } catch (err) {
      console.error('NotificationSetup: failed to load preferences', err);
      this.notificationPreferences.set(emptyNotificationPreferences());
      this.errorMessage.set('Could not load notification preferences. Please try again.');
    } finally {
      this.isLoading.set(false);
    }
  }

  async onSkip(): Promise<void> {
    this.contextService.clearPendingNotificationSetup();
    await this.router.navigate(['/dashboard'], { replaceUrl: true });
  }

  async onSave(): Promise<void> {
    const preferences = this.notificationPreferences();
    if (!preferences.mCode) {
      return;
    }

    this.errorMessage.set(null);
    this.isSaving.set(true);
    try {
      await firstValueFrom(
        this.notificationPreferencesService
          .save(buildSaveNotificationPreferencesRequest(this.notificationForm, preferences))
          .pipe(timeout(20000))
      );
      await firstValueFrom(
        this.contextService.loadUserContext(this.config.portalKey).pipe(timeout(20000))
      );
      this.contextService.clearPendingNotificationSetup();
      await this.router.navigate(['/dashboard'], { replaceUrl: true });
    } catch (err) {
      console.error('NotificationSetup: save failed', err);
      if (err instanceof HttpErrorResponse && err.status === 400) {
        await this.loadForSelectedClient();
        const detail = notificationPreferencesValidationError(err);
        this.errorMessage.set(
          detail ?? 'Those notification choices are not allowed for your account.'
        );
      } else {
        this.errorMessage.set('Could not save notification preferences. Please try again.');
      }
    } finally {
      this.isSaving.set(false);
    }
  }
}
